import { Component, Input, OnInit, ViewChild } from '@angular/core';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { StudiesService } from '../studies.service';
import { ColumnsConfig, TableConfig } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SitesService } from 'src/app/sites/sites.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';
import { UsersService } from 'src/app/users/users.service';

@Component({
	selector: 'app-studies-section',
	templateUrl: './studies-active-completed.component.html',
})
export class StudiesSectionComponent implements OnInit {

	@Input() mode: 'active' | 'completed' = 'active';

	@ViewChild('locationModal') locationModal: ModalComponent;

	allStudies: any[] = [];
	tableData: any[] = [];
	selectedLocation: any = null;
	isShownMap: boolean = false;
	showNewStudyForm: boolean = false;
	showEditStudyForm: boolean = false;
	editStudyData: any = null;

	invalidDateRange: boolean = false;
	dateRange: any = null;

	selectedSN: string = 'all';
	snOptions: { id: string, value: string }[] = [];
	currentUser: any = {};
	listingColumns: ColumnsConfig[] = [];

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		start_study_time_fmt: TimeSource.Utc,
	};

	get title(): string {
		return this.translateService.instant(
			this.mode === 'active' ? 'studies.active_studies_title' : 'studies.completed_studies_title'
		);
	}

	get isActive(): boolean {
		return this.mode === 'active';
	}

	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fileName: '',
		draggable: true,
	};

	constructor(
		private studiesService: StudiesService,
		private translateService: TranslateService,
		public sitesService: SitesService,
		private commonService: CommonService,
		private usersService: UsersService,
	) {}

	ngOnInit() {
		this.tableConfig.fileName = this.title;
		this.listingColumns = this.getListingColumns();
		this.commonService.applyTimeSourceTooltips(this.listingColumns, this.timeSourceByColumn);
		this.currentUser = this.usersService.getCurrentUser();
	}

	getListingColumns(): ColumnsConfig[] {
		return [
			{ key: 'name', name: this.translateService.instant('studies.name'), type: 'link' },
			{ key: 'customer_name', name: this.translateService.instant('studies.customer_name'), type: 'string' },
			{ key: 'site_name', name: this.translateService.instant('studies.site_name'), type: 'link' },
			{ key: 'serial_number', name: this.translateService.instant('studies.iotah_ps_sn'), type: 'link', noValueString: '-' },
			{ key: 'truck_id', name: this.translateService.instant('studies.iotah_ps_id'), type: 'link', noValueString: '-' },
			{ key: 'start_study_time_fmt', name: this.translateService.instant('studies.start_study_time'), type: 'date', noValueString: '-' },
			{ key: 'last_connect_time_fmt', name: `${this.translateService.instant('studies.last_connect_time')} (${this.commonService.showUserTimeZoneReference()})`, type: 'date', noValueString: '-' },
			{ key: 'last_latitude', name: this.translateService.instant('studies.last_lat'), type: 'action_link', noValueString: '-', hasSort: false, hasFilter: false },
			{ key: 'last_longitude', name: this.translateService.instant('studies.last_lon'), type: 'action_link', noValueString: '-', hasSort: false, hasFilter: false },
			{ key: 'action', name: this.translateService.instant('g.action'), type: 'icon', hasSort: false, hasFilter: false },
		];
	}

	getData() {
		if (this.invalidDateRange || !this.dateRange)
			return;

		const status = this.mode === 'active' ? 0 : 1;
		const params: any = { parent: 'admin', status, request_status: this.studiesService.REQUEST_STATUS.accepted };
		params.from = moment(this.dateRange.fromDate).utc().unix();
		params.to = moment(this.dateRange.toDate).utc().unix();

		this.studiesService.getStudies(params).subscribe((res: any) => {
			this.allStudies = res;
			this.buildSnOptions();
			this.filterAndFormatData();
		});
	}

	buildSnOptions() {
		const sns: string[] = [...new Set<string>(
			this.allStudies.map((s: any) => s.serial_number).filter(Boolean)
		)].sort();

		const validSnOptions = sns.filter(sn => sn && sn.trim() !== '' && sn.trim() !== '---');
		this.snOptions = [
			{ id: 'all', value: this.translateService.instant('g.all') },
			...validSnOptions.map(sn => ({ id: sn, value: sn }))
		];
	}

	filterAndFormatData() {
		let filtered = this.allStudies;

		if (this.selectedSN !== 'all')
			filtered = filtered.filter((s: any) => s.serial_number === this.selectedSN);

		this.tableData = this.formatData(filtered);
	}

	formatData(studies: any[]): any[] {
		return studies.map(study => {
			const deviceLink = (study.device_customer_id && study.device_site_id && study.mac_address)
				? ['/', study.device_customer_id, study.device_site_id, study.mac_address, 'performance']
				: null;

			const shouldDisablePowerStudySiteLink =
				(!this.currentUser.isSCT ||
				!this.usersService.hasAccessFunction('has_power_study')) &&
				study.site_name == this.commonService.powerStudiesDevicesSiteName

			const siteLink = (study.has_study_site_access && !shouldDisablePowerStudySiteLink) ? ['/', study.customer_id, study.site_id] : null;

			return {
				study,
				name: { value: study.name, link: study.has_study_site_access ? ['/', study.customer_id, study.site_id, study.mac_address, 'performance', study.id] : null },
				customer_name: { value: study.customer_name },
				site_name: { value: study.site_name, link: siteLink },
				serial_number: { value: study.serial_number, link: study.has_site_device_acces ? deviceLink : null },
				truck_id: { value: study.device_truck_id, link: study.has_site_device_acces ? deviceLink : null },
				start_study_time_fmt: { value: study.start_study_time ? moment(study.start_study_time * 1000).utc().format('YYYY-MM-DD') : null },
				last_connect_time_fmt: { value: study.device_last_connect_time ? moment(study.device_last_connect_time * 1000).format('YYYY-MM-DD HH:mm') : null },
				last_latitude: study.device_last_latitude ? { value: study.device_last_latitude, action: () => this.openLocationModal(study) } : { value: null },
				last_longitude: study.device_last_longitude ? { value: study.device_last_longitude, action: () => this.openLocationModal(study) } : { value: null },
				action: study.has_site_device_acces && !study.is_done ? { icon: 'edit', action: () => { this.openEditStudy(study); } } : {},
			};
		});
	}

	onDatesChanged(event: any) {
		this.dateRange = event.dates;
		this.invalidDateRange = event.invalidRange;

		if (!this.invalidDateRange)
			this.getData();
	}

	onSnFilterChange() {
		this.filterAndFormatData();
	}

	onNewStudyFormClose(id: number) {
		this.showNewStudyForm = false;
		if (id > 0)
			this.getData();
	}

	openEditStudy(study: any) {
		this.editStudyData = study;
		this.showEditStudyForm = true;
	}

	onEditStudyFormClose(id: number) {
		this.showEditStudyForm = false;
		this.editStudyData = null;

		if (id)
			this.getData();
	}

	openLocationModal(study: any) {
		if (!study.device_last_latitude || !study.device_last_longitude)
			return;

		this.isShownMap = false;
		this.selectedLocation = [{
			latitude: study.device_last_latitude,
			longitude: study.device_last_longitude,
			type: 'device',
			serial_number: study.serial_number,
			mac_address: study.mac_address,
			sct_device_type: study.sct_device_type,
		}];

		this.locationModal.show();
		setTimeout(() => { this.isShownMap = true; }, 0);
	}
}
